import React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ScreeningTestLink from './ScreeningTestLink';
import { Container } from '@/components/ui/container';
import { Stack, Inline } from '@/components/ui/layout';
import { Heading, Text } from '@/components/ui/typography';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { cn } from '@/lib/utils';
import { Users, Calendar, Briefcase } from 'lucide-react';

const PersonalityTest = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [scheduledDateTime, setScheduledDateTime] = useState(''); // State for Date/Time
  const [role, setRole] = useState(''); // State for Role
  const [testId, setTestId] = useState(null);
  const [showPopup, setShowPopup] = useState(false);



  const handleClosePopup = () => {
    setShowPopup(false);
  };

  // Fetch users from the backend
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch("/api/select_users_personal_test");
        if (!response.ok) {
          throw new Error("Failed to fetch users");
        }
        const data = await response.json();
        console.log(response);
        setUsers(data);
      } catch (err) {
        console.error("Error fetching users:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Handle user selection
  const handleSelectUser = (userId) => {
    setSelectedUsers((prevSelected) =>
      prevSelected.includes(userId)
        ? prevSelected.filter((id) => id !== userId) // Deselect user
        : [...prevSelected, userId] // Select user
    );
  };

  const handleNext = async () => {
    if (!scheduledDateTime || !role || selectedUsers.length === 0) {
      alert('Please fill all fields and select users.');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('scheduledTime', scheduledDateTime);
      formData.append('role', role);
      selectedUsers.forEach(user => formData.append('selected_users', user));

      const response = await fetch("/api/select_users_personal_test", {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error("Failed to schedule test");

      const data = await response.json();
      setTestId(data.test_id);
      setShowPopup(true); // Show popup with the test ID
    } catch (error) {
      console.error("Error scheduling test:", error);
      alert("An error occurred while scheduling the test. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Text>Loading users...</Text>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col overflow-hidden">
      <header className="relative w-full pb-[90px] px-[59px] pt-9">
        <img
          src="https://cdn.builder.io/api/v1/image/assets/TEMP/7d62c9f26a60b045374ff77c2f5a16bfdc187928f083e4d609410759efabc347?placeholderIfAbsent=true&apiKey=e90a33775c2844879a20708b8df7376f"
          alt=""
          className="absolute inset-0 h-[95%] w-full object-cover object-center"
        />
        <div className="relative z-10">
          <div className="flex w-[92%] items-center justify-between gap-10">
            <Inline spacing="sm" align="center">
              <img
                src="https://cdn.builder.io/api/v1/image/assets/TEMP/e539cc78-8988-497d-bc1d-59bf9a489b74?placeholderIfAbsent=true&apiKey=e90a33775c2844879a20708b8df7376f"
                alt=""
                className="w-10 h-10"
              />
              <Text className="text-white text-xs tracking-wide">INTERVIEW MACHINE</Text>
            </Inline>
            <nav className="flex gap-8 text-white/90">
              {['Home', 'About', 'FAQ', 'Testimonial', 'Contact'].map(item => (
                <a
                  key={item}
                  href={`#${item.toLowerCase()}`}
                  className="hover:text-white transition-colors"
                >
                  {item}
                </a>
              ))}
            </nav>
            <Inline spacing="sm" align="center">
              <div className="w-15 h-15 rounded-full bg-muted" />
              <Text className="text-white font-medium">Company Name</Text>
            </Inline>
          </div>
          <Stack spacing="md" align="center" className="text-center mt-[122px]">
            <Heading level="h1" className="text-5xl md:text-6xl font-bold text-white tracking-wide">
              Schedule Your Personality & Behavioral Test
            </Heading>
            <Text size="lg" className="text-white/80 max-w-2xl font-semibold tracking-wide mt-7">
              Select users, specify their experience and skills, and assign roles to personalize the test scheduling process for deeper insights.
            </Text>
          </Stack>
        </div>
      </header>

      <main className="flex-1 px-0 max-w-[1130px] mx-auto w-full">
        <section className="flex flex-col md:flex-row gap-8 p-8">
          <Card className="flex-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Stack spacing="sm">
                {users.map((user) => (
                  <div key={user.id} className="flex items-center gap-2 p-2 hover:bg-muted/50 rounded transition-colors">
                    <Checkbox
                      id={`user-${user.id}`}
                      checked={selectedUsers.includes(user.id)}
                      onCheckedChange={() => handleSelectUser(user.id)}
                    />
                    <Label htmlFor={`user-${user.id}`} className="cursor-pointer flex-1">
                      {user.email}
                    </Label>
                  </div>
                ))}
              </Stack>
            </CardContent>
          </Card>
          
          <Card className="flex-1">
            <CardHeader>
              <CardTitle>Schedule Test</CardTitle>
            </CardHeader>
            <CardContent>
              <Stack spacing="md">
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4" />
                    Date/Time:
                  </Label>
                  <Input
                    type="datetime-local"
                    value={scheduledDateTime}
                    onChange={(e) => setScheduledDateTime(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Briefcase className="w-4 h-4" />
                    Select Role:
                  </Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select Role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Developer">Developer</SelectItem>
                      <SelectItem value="Tester">Tester</SelectItem>
                      <SelectItem value="Manager">Manager</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleNext} disabled={!scheduledDateTime || !role || selectedUsers.length === 0}>
                  Next: Schedule Test
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </section>
      </main>
      
      <Dialog open={showPopup} onOpenChange={setShowPopup}>
        <DialogContent>
          <ScreeningTestLink testId={testId} selectedTestCard={{ route: "/personality-test" }} onClose={handleClosePopup} />
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default PersonalityTest;